import { Button, Card } from "@nfp-tracker/components";
import { ArrowLeft } from "lucide-react";
import { useEffect, useRef, type ComponentType } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useInstructorClient } from "../../components/InstructorClientContext";
import { LoadingSkeleton } from "../../components/LoadingSkeleton";
import { ROUTES, type InstructorClientTab } from "../../constants/routes";
import { useOwnerContext } from "../../hooks/useOwnerContext";
import { logInstructorClientView } from "../../integrations/supabase/instructor";
import Calendar from "../Calendar";
import CycleHistory from "../CycleHistory";
import Dashboard from "../Dashboard";

const TAB_CONTENT: Record<InstructorClientTab, ComponentType> = {
  // The client's chart is the existing tracking UI rendered against the client's owner context.
  // Read-only comes from `useOwnerContext`, which forces `canWriteObservations` to false while
  // an instructor is viewing a client — these pages already gate every editing affordance on it.
  calendar: Calendar,
  insights: Dashboard,
  history: CycleHistory,
};

/**
 * The chart itself is just the personal page for that tab; the client's name, the read-only
 * badge, and the client switcher live in the shell's ClientViewBanner. This page only owns the
 * per-client bookkeeping: the view log and the "not your client" bounce.
 */
const InstructorClientChart = ({ tab }: { tab: InstructorClientTab }) => {
  const { clientUserId = "" } = useParams();
  const navigate = useNavigate();
  const { activeClient, isLoading, isError } = useInstructorClient();
  const ownerContext = useOwnerContext().data;
  const loggedClientIdRef = useRef<string | null>(null);

  const summary = activeClient?.clientUserId === clientUserId ? activeClient.summary : null;

  // One log entry per client per mount-chain, not per tab switch: the log is meant to read as
  // "opened this chart", not as a request trace.
  useEffect(() => {
    if (!summary || loggedClientIdRef.current === summary.clientUserId) return;

    loggedClientIdRef.current = summary.clientUserId;
    void logInstructorClientView(summary.clientUserId);
  }, [summary]);

  // A null summary after loading means the link is gone or was never theirs — RLS would refuse
  // the underlying reads anyway, so bounce back to the roster rather than render an empty chart.
  const isMissing = Boolean(clientUserId) && !isLoading && !isError && summary === null;
  useEffect(() => {
    if (isMissing) {
      navigate(ROUTES.instructorClients, { replace: true });
    }
  }, [isMissing, navigate]);

  if (isError) {
    return (
      <Card className="space-y-3 p-6">
        <p className="text-sm text-muted-foreground">This client's chart could not be loaded.</p>
        <Button variant="outline" onClick={() => navigate(ROUTES.instructorClients)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to clients
        </Button>
      </Card>
    );
  }

  // Rendering the chart before the owner context has resolved *this* client would show the
  // instructor's own data — or the previously open client's — under this client's name.
  const isContextForThisClient =
    ownerContext?.status === "ready" && ownerContext.viewingClientUserId === clientUserId;
  if (!summary || activeClient?.clientUserId !== clientUserId || !isContextForThisClient) {
    return <LoadingSkeleton />;
  }

  const TabContent = TAB_CONTENT[tab];
  // Keyed by client: the tab pages hold derived state (full history, PDF export inputs) that
  // must not carry over from one client to the next when switching without leaving the tab.
  return <TabContent key={clientUserId} />;
};

export default InstructorClientChart;
